/* eslint-disable no-unused-vars */
import { Table } from 'antd'
import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import { DAYS_OF_WEEK } from '../../../constants'
import {
  EventsByDay,
  ICell,
  IEvent,
  INorm,
  INormOfWeek,
  ITableColumn
} from '../../../types'
import { calculateNormOfWeek, daysOfWeekKeys } from '../../../utils'
import styles from '../SAPPCalendar.module.css'
import MonthlyCell from './MonthlyCell'
import { useState } from 'react'

interface IMonthlyCalendarProps {
  currentDate: Dayjs
  startDate: Dayjs
  endDate: Dayjs
  events: IEvent[]
  norms: INorm[]
  showWeeklyNorm: boolean
  onOpenDetail?: (date: Date, events: IEvent[]) => void
  onOpenCreate?: (date: Date) => void
  onEventDetail?: (event: IEvent) => void
  loading?: boolean
  eventDetail?: {event: IEvent | null, date: Dayjs} | null
  setEventDetail?: (params: {event: IEvent | null, date: Dayjs}) => void
}

const getEventsOfWeek = (events: IEvent[], targetDate: Dayjs): EventsByDay =>
  daysOfWeekKeys.reduce((acc: EventsByDay, day, index) => {
    const currentDate = targetDate.add(index, 'day')

    acc[day] = {
      events: events.filter((event) =>
        dayjs(event.startDate).isSame(currentDate, 'date')
      ),
      date: currentDate
    }
    return acc
  }, {} as EventsByDay)

const getCalendarBoundaries = (currentDate: Dayjs) => {
  const rawStart = currentDate.startOf('month')
  const rawEnd = currentDate.endOf('month')
  // Đảm bảo tuần bắt đầu từ thứ Hai
  const startDate =
    rawStart.day() === 0
      ? rawStart.subtract(6, 'day') // Chủ nhật -> lùi 6 ngày về thứ 2
      : rawStart.subtract(rawStart.day() - 1, 'day') // Các ngày khác -> lùi về thứ 2

  // Đảm bảo tuần kết thúc đến hết Chủ nhật
  const endDate =
    rawEnd.day() === 0 ? rawEnd : rawEnd.add(7 - rawEnd.day(), 'day') // Tiến tới Chủ nhật gần nhất

  return { startDate, endDate }
}

const MonthlyCalendar = ({
  currentDate,
  events,
  norms,
  showWeeklyNorm,
  onOpenDetail,
  onOpenCreate,
  onEventDetail,
  loading,
  eventDetail,
  setEventDetail
}: IMonthlyCalendarProps) => {
  const getTableColumns = () => {
    const weeklyNormColumn: ITableColumn = {
      title: (
        <div
          className={`${styles['monthly-calendar__title']} ${styles['monthly-calendar__title-active']}`}
        >
          Weekly Norm
        </div>
      ),
      dataIndex: 'norm',
      key: 'norm',
      width: '100px',
      render: (value: INormOfWeek) => {
        const { normOfWeek, totalNorm } = value
        return (
          <div className={styles['weekly-norm__cell']}>
            <div
              className={clsx(
                styles['weekly-norm__value'],
                normOfWeek > totalNorm && styles['!text-error']
              )}
            >
              {`${normOfWeek}/${totalNorm}`}
            </div>
          </div>
        )
      }
    }

    const dayColumns: ITableColumn[] = Object.values(DAYS_OF_WEEK).map(
      (day, index) => ({
        title: (
          <div className={styles['monthly-calendar__title']}>
            {day.slice(0, 3)}
          </div>
        ),
        dataIndex: day,
        key: day,
        width: 'calc((100% - 100px) / 7)',
        render: (value: ICell) => {
          return (
            <MonthlyCell
              key={index}
              events={value.events}
              date={value.date}
              currentDate={currentDate}
              onOpenDetail={onOpenDetail}
              onOpenCreate={onOpenCreate}
              onEventDetail={onEventDetail}
              eventDetail = {eventDetail}
              setEventDetail={setEventDetail}
            />
          )
        }
      })
    )

    return showWeeklyNorm ? [weeklyNormColumn, ...dayColumns] : dayColumns
  }

  const getWeeksOfFullMonth = (startDate: Dayjs, endDate: Dayjs) => {
    const weeks: Dayjs[] = []
    let targetDate = startDate.clone()

    while (targetDate.isBefore(endDate) || targetDate.isSame(endDate, 'day')) {
      weeks.push(targetDate)
      targetDate = targetDate.add(1, 'week')
    }

    return weeks
  }

  const getTableData = () => {
    const { startDate, endDate } = getCalendarBoundaries(currentDate)
    const weeks = getWeeksOfFullMonth(startDate, endDate)

    const data = weeks.map((week, index) => {
      const eventsOfWeek = getEventsOfWeek(events, week)

      if (!showWeeklyNorm) {
        return {
          id: index,
          ...eventsOfWeek
        }
      }

      return {
        id: index,
        norm: calculateNormOfWeek(norms, events, week, week.endOf('week')),
        ...eventsOfWeek
      }
    })

    return data
  }

  return (
    <Table
      rowKey={(record) => record.id}
      columns={getTableColumns()}
      dataSource={getTableData()}
      pagination={false}
      rowHoverable={false}
      className={styles['monthly-calendar']}
      loading={loading}
      tableLayout='fixed'
       rowClassName={(record, index) => {
        const data = getTableData(); 
        return index === data.length - 1 ? 'table-row-last' : '';
  }}
    />
  )
}

export default MonthlyCalendar
